import type { HoComponents } from '@pos/api-client';
import { hoApi } from '../api';

type Schemas = HoComponents['schemas'];
export type EinvoiceSummary = Schemas['EinvoiceRequestSummaryDto'];
export type EinvoiceDetail = Schemas['EinvoiceRequestDetailDto'];
export type EinvoiceBuyerInput = Schemas['EinvoiceBuyerInput'];

export const einvoicesKey = ['ho', 'einvoices'] as const;

/** Every status a request can have (ADR 0048), in the order a request goes through them. */
export const EINVOICE_STATUSES = [
  'not_configured',
  'pending',
  'submitting',
  'uncertain',
  'awaiting_number',
  'issued',
  'failed',
] as const;

export interface EinvoiceFilters {
  status?: string;
  storeId?: string;
  needsAdjustment?: boolean;
  from?: string;
  to?: string;
  search?: string;
  page: number;
  pageSize: number;
}

export async function listEinvoices(f: EinvoiceFilters) {
  const { data, error } = await hoApi.GET('/api/v1/einvoice/requests', {
    params: {
      query: {
        Status: f.status || undefined,
        StoreId: f.storeId || undefined,
        NeedsAdjustment: f.needsAdjustment,
        From: f.from,
        To: f.to,
        Search: f.search || undefined,
        Page: f.page,
        PageSize: f.pageSize,
      },
    },
  });
  if (!data) throw error;
  return data;
}

const byId = (id: string) => ({ params: { path: { id } } });

export async function getEinvoice(id: string): Promise<EinvoiceDetail> {
  const { data, error } = await hoApi.GET('/api/v1/einvoice/requests/{id}', byId(id));
  if (!data) throw error;
  return data;
}

/** Sends it again: a failed one, or one waiting for a profile its store now has. */
export async function retryEinvoice(id: string): Promise<EinvoiceDetail> {
  const { data, error } = await hoApi.POST('/api/v1/einvoice/requests/{id}/retry', byId(id));
  if (!data) throw error;
  return data;
}

/** Sends an invoice held until its send time now, with its store's earlier held ones (ADR 0070). */
export async function sendEinvoiceNow(id: string): Promise<EinvoiceDetail> {
  const { data, error } = await hoApi.POST('/api/v1/einvoice/requests/{id}/send-now', byId(id));
  if (!data) throw error;
  return data;
}

/** Corrects the buyer of an invoice the provider refused, and sends it again (FR-EIN-05). */
export async function setEinvoiceBuyer(id: string, body: EinvoiceBuyerInput): Promise<EinvoiceDetail> {
  const { data, error } = await hoApi.PUT('/api/v1/einvoice/requests/{id}/buyer', { ...byId(id), body });
  if (!data) throw error;
  return data;
}

/** Emails the issued invoice: to the buyer, or to another address. */
export async function emailEinvoice(id: string, email: string | null): Promise<EinvoiceDetail> {
  const { data, error } = await hoApi.POST('/api/v1/einvoice/requests/{id}/email', { ...byId(id), body: { email } });
  if (!data) throw error;
  return data;
}

/** Clears the refund flag once HO has adjusted the invoice at the provider. */
export async function adjustmentDone(id: string, note: string): Promise<EinvoiceDetail> {
  const { data, error } = await hoApi.POST('/api/v1/einvoice/requests/{id}/adjustment-done', { ...byId(id), body: { note } });
  if (!data) throw error;
  return data;
}

/**
 * The invoice's PDF, fetched from the provider by HO (never stored), saved by the browser from the response: never a link
 * with a token in it.
 */
export async function downloadEinvoicePdf(id: string, fileName: string): Promise<void> {
  const { data, error, response } = (await hoApi.GET(
    '/api/v1/einvoice/requests/{id}/pdf' as never,
    { ...byId(id), parseAs: 'blob' } as never,
  )) as { data?: unknown; error?: unknown; response: Response };
  if (!response.ok || !(data instanceof Blob)) throw error ?? new Error(String(response.status));
  const url = URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}
